import { authService } from './authService';
import { userProfileService } from './userProfileService';
import { User } from '@/types/user';

/**
 * Authentication actions
 * These are the implementations of the auth-related actions that will be used in the store
 */
export const authActions = {
  /**
   * Initialize auth state
   */
  initAuth: async (set: any, get: any) => {
    console.log("[UserSlice] Initializing auth state");
    try {
      // Get current session
      const session = await authService.getCurrentSession();
      if (!session || !session.user) {
        console.log("[UserSlice] No active session found");
        set({ user: null, isAuthenticated: false });
        return;
      }
      
      console.log("[UserSlice] Active session found for user:", session.user.id);
      
      // Get user data from Supabase Auth
      const user = session.user;
      
      try {
        // Fetch profile data - this might fail if the table doesn't exist
        const profileData = await userProfileService.fetchUserProfile(user.id);
        
        // Fetch role info with improved error handling
        const roleInfo = await userProfileService.fetchUserRole(user.id);
        
        console.log("[UserSlice] Profile data:", profileData);
        console.log("[UserSlice] Role info:", roleInfo);
        
        // Format data and update state
        let roleId = typeof roleInfo?.roleId === 'string' ? roleInfo.roleId : 'user';
        let roleName = typeof roleInfo?.roleName === 'string' ? roleInfo.roleName : 'USER';
        
        // Use email as fallback name if no profile
        const displayName = profileData?.name || user.email!.split('@')[0];
        
        set({
          user: {
            id: user.id,
            email: user.email!,
            name: displayName,
            avatar_url: profileData?.avatar_url,
            role: roleId,
            role_name: roleName,
            dealer_activation_status: profileData?.dealer_activation_status || 'NONE',
          },
          isAuthenticated: true,
        });
        
        console.log("[UserSlice] Auth initialized with user:", user.id);
      } catch (error) {
        console.error("[UserSlice] Error fetching profile data:", error);
        // Still authenticate the user, just with minimal data
        set({
          user: {
            id: user.id,
            email: user.email!,
            name: user.email!.split('@')[0],
            role: 'user',
            role_name: 'USER',
          },
          isAuthenticated: true,
        });
      }
    } catch (error) {
      console.error("[UserSlice] Error initializing auth:", error);
      set({ user: null, isAuthenticated: false });
    }
  },
  
  /**
   * Login action
   */
  login: async (set: any, get: any, email: string, password: string) => {
    set({ isLoading: true });
    try {
      // Call auth service to sign in
      const user = await authService.signIn(email, password);
      
      if (user) {
        try {
          // Fetch user profile - might fail if table doesn't exist
          const profileData = await userProfileService.fetchUserProfile(user.id);
          
          // Fetch user role with improved method
          const roleInfo = await userProfileService.fetchUserRole(user.id);
          
          console.log("[UserSlice] User ID:", user.id);
          console.log("[UserSlice] Profile data:", profileData);
          console.log("[UserSlice] Role info:", roleInfo);
          
          // Ensure we have valid role info
          let roleId = typeof roleInfo?.roleId === 'string' ? roleInfo.roleId : 'user';
          let roleName = typeof roleInfo?.roleName === 'string' ? roleInfo.roleName : 'USER';
          
          console.log("[UserSlice] Normalized role data:", { roleId, roleName });
          
          // Use email as fallback name if no profile
          const displayName = profileData?.name || user.email!.split('@')[0];
          
          // Update state with user info
          set({ 
            user: {
              id: user.id,
              email: user.email!,
              name: displayName,
              avatar_url: profileData?.avatar_url,
              role: roleId,
              role_name: roleName,
              dealer_activation_status: profileData?.dealer_activation_status || 'NONE',
            },
            isAuthenticated: true,
          });
          
          // Log the final user object being set in store
          console.log("[UserSlice] User set in store:", {
            id: user.id,
            email: user.email,
            role: roleId,
            role_name: roleName
          });
        } catch (error) {
          console.error("[UserSlice] Error fetching profile data:", error);
          // Still authenticate the user, just with minimal data
          set({ 
            user: {
              id: user.id,
              email: user.email!,
              name: user.email!.split('@')[0],
              role: 'user',
              role_name: 'USER',
            },
            isAuthenticated: true,
          });
        }
      }
    } catch (error) {
      console.error('[UserSlice] Login error:', error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  /**
   * Logout action
   */
  logout: async (set: any, get: any) => {
    console.log("[UserSlice] Logging user out");
    set({ isLoading: true });
    
    // Immediately clear user state first for responsive UI
    set({ 
      user: null,
      isAuthenticated: false,
    });
    
    try {
      // Then try to clean up the server-side session
      // We don't await this because we want the UI to update immediately
      // and we don't want to block on potential errors
      authService.signOut().catch(error => {
        // Just log the error - we've already updated the UI state
        console.error('[UserSlice] Error during signOut process:', error);
      });
      
      console.log("[UserSlice] Logout process initiated");
    } finally {
      set({ isLoading: false });
    }
  },
  
  /**
   * Registration action
   */
  register: async (set: any, get: any, email: string, password: string, name: string) => {
    set({ isLoading: true });
    try {
      // Call auth service to sign up
      const user = await authService.signUp(email, password);
      
      if (user) {
        try {
          // Try to create user profile, but continue if it fails due to missing table
          await userProfileService.createUserProfile(user.id, user.email!, name);
        } catch (error) {
          console.error("[UserSlice] Error creating profile, continuing with auth:", error);
        }
        
        // Set user in state
        set({ 
          user: {
            id: user.id,
            email: user.email!,
            name,
            role: 'user',
            role_name: 'USER',
          },
          isAuthenticated: true,
        });
        
        console.log("[UserSlice] User registered and set in store:", {
          id: user.id,
          email: user.email,
          role: 'user',
          role_name: 'USER'
        });
      }
    } catch (error) {
      console.error('[UserSlice] Registration error:', error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  /**
   * Set user action
   */
  setUser: (set: any, get: any, user: User | null) => {
    set({ 
      user, 
      isAuthenticated: !!user 
    });
  }
};
